import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../auth/Authprovider";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import { membershipApi, type ProductCreationQuota } from "@b2b/services";
import { StoreProductDialog } from "@b2b/ui-components";
import { Button } from "@b2b/ui-components";
import { ArrowLeft, Loader2, ChevronLeft, ChevronRight, Save, Eye } from "lucide-react";
import {
  getProductSaveErrorMessage,
  prepareProductPayloadForApi,
} from "../../utils/productPayload";
import { dispatchNotificationUnreadRefresh } from "../../notifications/notificationUnreadEvents";

/**
 * Unified CreateProductPage component that works for both Admin and Seller roles.
 * Automatically detects the role based on the current route path.
 */
export default function CreateProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const { companyId } = useAuth();
  
  // Determine if we're in admin mode based on route path
  const isAdminMode = location.pathname.startsWith("/admin");
  
  // Use appropriate service based on mode
  const productService = isAdminMode ? adminService : sellerService;
  
  // Navigation paths based on mode
  const navigatePath = isAdminMode 
    ? "/admin/admin_products" 
    : "/seller/product_management";
  
  const isEdit = !!id;
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Seller-only: restrict category selection to industries allowed by membership plan.
  const [allowedCategoryIds, setAllowedCategoryIds] = useState<string[] | undefined>(undefined);
  const [isMembershipIndustriesFetched, setIsMembershipIndustriesFetched] = useState(false);
  /** New seller products only: quota from API (blocks wizard when at limit / no plan). */
  const [productQuota, setProductQuota] = useState<ProductCreationQuota | null>(null);
  const [footerState, setFooterState] = useState<{
    currentStep: number;
    canGoNext: boolean;
    canGoPrev: boolean;
    isPublishEnabled: boolean;
    totalSteps: number;
    showNextButton?: boolean;
  } | null>(null);
  const footerHandlersRef = useRef<{
    onPrev: () => void;
    onNext: () => void;
    onSaveDraft: () => void;
    onPublish: () => void;
    onArchive?: () => void;
  }>({
    onPrev: () => {},
    onNext: () => {},
    onSaveDraft: () => {},
    onPublish: () => {},
  });

  // Fetch product data if editing
  useEffect(() => {
    const fetchProduct = async () => {
      if (isEdit && id) {
        try {
          setInitializing(true);
          const response = await productService.getProductById(id);
          if (response.success && response.data) {
            let data = { ...response.data };
            if (
              data.hasPendingUpdate &&
              data.pendingUpdateSnapshot &&
              typeof data.pendingUpdateSnapshot === "object"
            ) {
              data = {
                ...data,
                ...(data.pendingUpdateSnapshot as Record<string, unknown>),
              };
            }
            setProduct(data);
          } else {
            // Product not found or error
            navigate(navigatePath, { replace: true });
          }
        } catch (error) {
          console.error("Error fetching product:", error);
          navigate(navigatePath, { replace: true });
        } finally {
          setInitializing(false);
        }
      } else {
        setInitializing(false);
      }
    };

    fetchProduct();
  }, [id, isEdit, isAdminMode, navigate, productService, navigatePath]);

  useEffect(() => {
    if (isAdminMode) return;

    let cancelled = false;

    const fetchSellerMembershipAndQuota = async () => {
      try {
        setIsMembershipIndustriesFetched(false);
        if (!isEdit) {
          setProductQuota(null);
        }

        const subscriptionPromise = membershipApi
          .getCompanySubscription()
          .catch(() => null);
        const quotaPromise = !isEdit
          ? membershipApi.getProductCreationQuota().catch(() => null)
          : Promise.resolve(null);

        const [subscription, quota] = await Promise.all([
          subscriptionPromise,
          quotaPromise,
        ]);

        if (cancelled) return;

        const industries = Array.isArray(subscription?.company?.industryTypes)
          ? (subscription?.company?.industryTypes as string[])
          : [];
        setAllowedCategoryIds(industries.filter(Boolean));

        if (!isEdit && quota) {
          setProductQuota(quota);
        }
      } catch {
        if (cancelled) return;
        setAllowedCategoryIds([]);
        if (!isEdit) {
          setProductQuota(null);
        }
      } finally {
        if (cancelled) return;
        setIsMembershipIndustriesFetched(true);
      }
    };

    fetchSellerMembershipAndQuota();

    // Refresh when user returns from membership plan screen (dynamic update).
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") fetchSellerMembershipAndQuota();
    };
    document.addEventListener("visibilitychange", onVisibilityOrFocus);
    window.addEventListener("focus", onVisibilityOrFocus);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityOrFocus);
      window.removeEventListener("focus", onVisibilityOrFocus);
    };
  }, [isAdminMode, companyId, isEdit]);

  const sellerWizardLocked =
    !isAdminMode &&
    !isEdit &&
    !!productQuota &&
    (!productQuota.canAddProduct || productQuota.needsMembershipSetup);

  const handleSaveDraft = async (data: any) => {
    try {
      setLoading(true);
      const productData = prepareProductPayloadForApi({
        ...data,
        status: "draft",
        companyId: data.companyId || companyId,
        // Admin-specific fields
        ...(isAdminMode && {
          adminCreated: true,
          approvalStatus: "pending",
          isEnabled: false,
        }),
      });

      const response = isEdit
        ? await productService.updateProductToDraft(id!, productData)
        : await productService.saveProductAsDraft(productData);

      if (response.success) {
        if (!isAdminMode) {
          dispatchNotificationUnreadRefresh();
        }
        if (!isAdminMode && isEdit && response.data?.hasPendingUpdate) {
          alert(
            "Your draft changes were saved for admin review. The live listing stays unchanged until an admin approves."
          );
        }
        navigate(navigatePath, { replace: true });
      } else {
        throw new Error(response.message || "Failed to save product");
      }
    } catch (error: unknown) {
      console.error("Error saving product as draft:", error);
      alert(getProductSaveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (data: any) => {
    try {
      setLoading(true);
      const productData = prepareProductPayloadForApi({
        ...data,
        status: "active",
        companyId: data.companyId || companyId,
        // Admin-specific fields
        ...(isAdminMode && {
          adminCreated: true,
          approvalStatus: "approved",
          isEnabled: true,
        }),
      });

      let response;
      if (isEdit) {
        // Admin uses updateProductToPublished, Seller uses updateProductAsSeller
        if (isAdminMode) {
          response = await adminService.updateProductToPublished(id!, productData);
        } else {
          response = await sellerService.updateProductAsSeller(id!, productData);
        }
      } else {
        // Admin uses publishProduct, Seller uses createProductAsSeller
        if (isAdminMode) {
          response = await adminService.publishProduct(productData);
        } else {
          response = await sellerService.createProductAsSeller(productData);
        }
      }

      if (response.success) {
        if (!isAdminMode) {
          dispatchNotificationUnreadRefresh();
        }
        if (!isAdminMode && isEdit && response.data?.hasPendingUpdate) {
          alert(
            "Your changes were submitted for admin review. The live listing stays unchanged until an admin approves."
          );
        }
        navigate(navigatePath, { replace: true });
      } else {
        throw new Error(response.message || "Failed to publish product");
      }
    } catch (error: unknown) {
      console.error("Error publishing product:", error);
      alert(getProductSaveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      setLoading(true);
      const productData = prepareProductPayloadForApi({
        ...data,
        companyId: data.companyId || companyId,
        // Admin-specific fields
        ...(isAdminMode && {
          adminCreated: true,
        }),
      });

      // Admin uses updateProductAsAdmin, Seller uses updateProductAsSeller
      const response = isAdminMode
        ? await adminService.updateProductAsAdmin(id!, productData)
        : await sellerService.updateProductAsSeller(id!, productData);

      if (response.success) {
        if (!isAdminMode) {
          dispatchNotificationUnreadRefresh();
        }
        if (!isAdminMode && isEdit && response.data?.hasPendingUpdate) {
          alert(
            "Your changes were submitted for admin review. The live listing stays unchanged until an admin approves."
          );
        }
        navigate(navigatePath, { replace: true });
      } else {
        throw new Error(response.message || "Failed to update product");
      }
    } catch (error: unknown) {
      console.error("Error updating product:", error);
      alert(getProductSaveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Prefer returning to the actual previous page; fallback keeps current behavior
    // for direct/deep links where there is no in-app history entry.
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) {
      navigate(-1);
      return;
    }
    navigate(navigatePath, { replace: true });
  };

  // Memoize footer state change handler to prevent infinite loops
  const handleFooterStateChange = useCallback((state: {
    currentStep: number;
    canGoNext: boolean;
    canGoPrev: boolean;
    isPublishEnabled: boolean;
    totalSteps: number;
  }) => {
    setFooterState(state);
  }, []);

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 bg-gray-50 flex flex-col min-h-[calc(100vh-64px)]">
      {/* Page Header - Alibaba Style */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm w-full min-w-0">
        <div className="max-w-[1400px] mx-auto w-full min-w-0 px-3 py-3 sm:px-4 sm:py-4 md:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="w-fit shrink-0 flex items-center gap-2 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                  {isEdit ? "Edit Product" : "Create Product"}
                </h1>
                <p className="text-xs text-gray-500 mt-1 sm:text-sm">
                  {isEdit
                    ? "Update product information"
                    : "Fill in the details to create a new product"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isAdminMode && !isEdit && sellerWizardLocked && productQuota && (
        <div className="bg-amber-50 border-b border-amber-200 w-full min-w-0">
          <div className="max-w-[1400px] mx-auto w-full min-w-0 px-3 py-3 sm:px-4 md:px-6 md:py-4">
            <p className="text-sm font-medium text-amber-900">
              {productQuota.needsMembershipSetup
                ? "Select or complete a membership plan before you can add products."
                : productQuota.maxProducts != null
                  ? `You’ve used all product slots for your plan (${productQuota.lifetimeCreated ?? productQuota.currentCount} of ${productQuota.maxProducts} total creations). Deleting products does not free slots—upgrade your plan to add more.`
                  : "You can’t add another product with your current plan."}
            </p>
            <Link
              to="/seller/membership_plans"
              className="inline-block mt-2 text-sm font-semibold text-orange-700 hover:text-orange-800 underline"
            >
              Go to membership plans
            </Link>
          </div>
        </div>
      )}

      {/* Form Content - Full Page Layout */}
      <div className="flex-1 flex flex-col min-h-0 w-full min-w-0">
        <div className="max-w-[1400px] mx-auto w-full min-w-0 px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6 flex-1 flex flex-col">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0 min-w-0">
            <StoreProductDialog
              product={product}
              isEdit={isEdit}
              isAdminMode={isAdminMode}
              companyId={companyId}
              isLoading={loading}
              lockWizard={sellerWizardLocked}
              allowedCategoryIds={
                !isAdminMode && isMembershipIndustriesFetched ? allowedCategoryIds : undefined
              }
              onSaveDraft={handleSaveDraft}
              onPublish={handlePublish}
              onUpdate={handleUpdate}
              open={true}
              renderAsPage={true}
              onOpenChange={(open) => {
                if (!open) {
                  handleCancel();
                }
              }}
              onFooterStateChange={handleFooterStateChange}
              footerHandlers={footerHandlersRef.current || undefined}
            />
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      {footerState && (
        <div className="sticky bottom-0 bg-white border-t shadow-lg z-50 w-full min-w-0">
          <div className="max-w-[1400px] mx-auto w-full min-w-0 px-3 py-3 sm:px-4 md:px-6 md:py-4">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (footerHandlersRef.current?.onPrev) {
                      footerHandlersRef.current.onPrev();
                    }
                  }}
                  disabled={!footerState.canGoPrev}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                {footerState.currentStep < footerState.totalSteps - 1 &&
                  footerState.showNextButton !== false && (
                    <Button
                      type="button"
                      onClick={() => {
                        if (footerHandlersRef.current?.onNext) {
                          footerHandlersRef.current.onNext();
                        }
                      }}
                      variant="primary"
                      disabled={!footerState.canGoNext}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => footerHandlersRef.current?.onSaveDraft()}
                  disabled={loading || sellerWizardLocked}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save as Draft
                </Button>

                <Button
                  type="button"
                  onClick={() => footerHandlersRef.current?.onPublish()}
                  variant="primary"
                  disabled={
                    loading || sellerWizardLocked
                  }
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  {isEdit ? "Update Product" : "Publish Product"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

